/** @jsxImportSource @emotion/react */
import { alpha, Box, Slider as MuiSlider, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import {
  circleTick,
  indicatorContainer,
  indicatorsContainer,
  sliderContainer,
  sliderWithIndicators,
  tickContainer,
  tickValue,
  triangleTick,
} from './Slider.styles'
import { SliderIndicator } from './SliderIndicator'
import { useEffect, useMemo, useState } from 'react'
import { Tooltip } from '@app/components/Atoms/Tooltip/Tooltip'

export type SliderMark = {
  value: number
  label: string
  disabled?: boolean
}

export type SliderIndicator = {
  value: number
  label: string
  tooltip?: string
}

export interface SliderProps {
  marks: SliderMark[]
  defaultValue?: number | number[]
  valueLabelDisplay?: 'on' | 'off' | 'auto'
  orientation?: 'vertical' | 'horizontal'
  sliderIndicators?: SliderIndicator[]
  ariaLabel?: string
  disabled?: boolean
  minimalLabels?: boolean
  useTriangleTicks?: boolean
  value: number | number[]
  useOnChangeCommitted?: boolean
  handleValueChange: (value: number) => void
  track?: 'normal' | 'inverted' | false
}

const ConditionalNoDataTooltip = ({
  condition,
  children,
}: {
  condition: boolean
  children: React.ReactElement
}): React.ReactElement =>
  condition ? (
    <Tooltip placement="top" title="No Data">
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  )

export const Slider = ({
  ariaLabel = 'slider',
  marks,
  defaultValue,
  value,
  handleValueChange,
  useOnChangeCommitted = false,
  sliderIndicators = [],
  orientation = 'horizontal',
  disabled = false,
  minimalLabels = false,
  useTriangleTicks = false,
  track = 'normal',
}: SliderProps) => {
  const theme = useTheme()
  const [sliderValue, setSliderValue] = useState<number | number[]>(value)

  const values = useMemo(() => marks.map((mark) => mark.value), [marks])
  const validValues = useMemo(
    () => marks.flatMap((mark) => (mark.disabled ? [] : mark.value)),
    [marks],
  )
  const highestValue = useMemo(() => Math.max(...values), [values])
  const lowestValue = useMemo(() => Math.min(...values), [values])
  const displayIndicators = !!sliderIndicators.length && orientation === 'horizontal' // TODO:indicators for vertical sliders

  const getIndicatorXPositionPercentage = (min: number, max: number) => (value: number) =>
    (100 * (value - min)) / (max - min)

  function valueLabelFormat(value: number) {
    const marker = marks.findIndex((mark) => mark.value === value)
    return marks[marker]?.label ?? 'Not available'
  }

  const getValidValue = (newValue: number) => {
    if (validValues.includes(newValue)) return newValue
    // If the new value is non-selectable, find the nearest selectable value
    if (newValue === 0 && validValues.includes(0)) return 0
    const nearestSelectable = validValues.reduce((prevValue, currValue) => {
      const numCurr = typeof currValue === 'string' ? parseFloat(currValue) : currValue
      const numPrev = typeof prevValue === 'string' ? parseFloat(prevValue) : prevValue
      return Math.abs(numCurr - newValue) < Math.abs(numPrev - newValue) ? currValue : prevValue
    }, validValues[0])
    return nearestSelectable
  }
  const handleSliderValueChange = (value: number) => {
    const validValue = getValidValue(value)
    setSliderValue(validValue)
    if (!useOnChangeCommitted) handleValueChange(validValue)
  }

  const handleSliderValueCommited = (value: number) => {
    if (useOnChangeCommitted) handleValueChange(getValidValue(value))
  }

  const minimalMarks = useMemo(() => {
    // Determine best layout of marks by hiding non-important or close marks
    const visibleMarks: Record<number, boolean | undefined> = Object.fromEntries(
      marks.map((m) => [m.value, undefined]),
    )
    const sliderRange = highestValue - lowestValue
    const showAndHideAdjacentMarks = (value: number | number[]) => {
      if (Array.isArray(value)) value = value[0]
      if (visibleMarks[value] !== undefined) return
      visibleMarks[value] = true
      const index = marks.findIndex((m) => m.value === value)
      if (index === -1) return
      // scan lower
      for (let i = index - 1; i >= 0; i--) {
        if (
          Math.abs(marks[i].value - value) / sliderRange < 0.2 &&
          visibleMarks[marks[i].value] === undefined
        ) {
          visibleMarks[marks[i].value] = false
        }
      }
      // scan higher
      for (let i = index + 1; i < marks.length; i++) {
        if (
          Math.abs(marks[i].value - value) / sliderRange < 0.2 &&
          visibleMarks[marks[i].value] === undefined
        ) {
          visibleMarks[marks[i].value] = false
        }
      }
    }
    showAndHideAdjacentMarks(value)
    showAndHideAdjacentMarks(lowestValue)
    showAndHideAdjacentMarks(highestValue)
    while (Object.values(visibleMarks).includes(undefined)) {
      const nextValue = Object.keys(visibleMarks).find(
        (key) => visibleMarks[parseFloat(key)] === undefined,
      )
      if (nextValue) showAndHideAdjacentMarks(parseFloat(nextValue))
    }
    return marks.map((mark) => {
      const isSelected = value === mark.value
      return {
        ...mark,
        label: (
          <Box key={mark.value} css={tickContainer}>
            {useTriangleTicks ? (
              value !== mark.value && (
                <ConditionalNoDataTooltip condition={!!mark.disabled}>
                  <Typography
                    color={mark.disabled ? `${alpha(theme.palette.text.disabled, 0.5)}` : 'initial'}
                    css={triangleTick()}
                  >
                    ▲
                  </Typography>
                </ConditionalNoDataTooltip>
              )
            ) : (
              <ConditionalNoDataTooltip condition={!!mark.disabled}>
                <Box css={circleTick({ theme, disabled: mark.disabled })} />
              </ConditionalNoDataTooltip>
            )}
            <Typography
              color={mark.disabled ? `${alpha(theme.palette.text.disabled, 0.5)}` : 'initial'}
              css={tickValue({
                theme,
                disabled: mark.disabled,
                isSelected,
                useTriangleTicks,
                show: !!visibleMarks[mark.value],
              })}
            >
              {mark.label}
            </Typography>
          </Box>
        ),
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marks, theme, useTriangleTicks, value])

  useEffect(() => {
    setSliderValue(value)
  }, [value])

  return (
    <Box css={sliderContainer({ track, theme, orientation })}>
      <Box css={indicatorsContainer}>
        {displayIndicators &&
          sliderIndicators.map((indicator, index) => {
            const indicatorXPosition = getIndicatorXPositionPercentage(
              lowestValue,
              highestValue,
            )(indicator.value)

            return (
              <Box
                key={index}
                css={indicatorContainer({
                  indicatorXPosition: indicatorXPosition - indicatorXPosition / 50,
                })}
              >
                <SliderIndicator
                  arrowPosition="up"
                  label={indicator.label}
                  tooltip={indicator.tooltip}
                />
              </Box>
            )
          })}
      </Box>

      <MuiSlider
        css={displayIndicators && sliderWithIndicators({ orientation })}
        getAriaLabel={() => ariaLabel}
        step={null}
        min={lowestValue}
        max={highestValue}
        marks={minimalLabels ? minimalMarks : marks}
        defaultValue={defaultValue}
        valueLabelFormat={(value) => valueLabelFormat(value)}
        getAriaValueText={(value) => valueLabelFormat(value)}
        orientation={orientation}
        disabled={disabled}
        value={sliderValue}
        onChange={(_, value) => handleSliderValueChange(value as number)}
        onChangeCommitted={(_, value) => handleSliderValueCommited(value as number)}
        track={track}
      />
    </Box>
  )
}
